import { useState, useRef } from "react";
import Slider from "react-slick";
import PropTypes from "prop-types";
import { updateUser } from "utils/updateUser";
import { navigateToLessonsPlan } from "utils/navigateToLessonsPlan";
import { obfuscateEmail } from "utils/obfuscateEmail";

import "./Cards.scss";

import Card from "components/organisms/Card";
import lang from "./lang";

const Cards = (props) => {
  const [state, setState] = useState({
    cardAddress: props?.cardAddress,
    lesson: props?.lesson,
    articles: props?.articles,
    streamlinedArticles: props?.streamlinedArticles,
    globals: props?.globals,
    cunningMode: localStorage?.getItem("cunningMode") === "true",
    authUser: props?.authUser,
  });
  const baseUrl = props?.baseUrl;
  const [
    startSpecialOfferCountDownCardIndex,
    setStartSpecialOfferCountDownCardIndex,
  ] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const updateUrl = (updatedIndex) => {
    const updatedUrl = `${baseUrl}/${props?.cardAddress?.lessonId}/${updatedIndex}/`;
    window.history.pushState(null, "", updatedUrl);
    setState({
      ...state,
      cardAddress: { ...state?.cardAddress, cardId: updatedIndex },
    });
  };
  const cardsSlider = useRef(null);
  const reportAnalytics = () => {
    window.dataLayer.push({
      event: "pageview",
      page_location: window?.location?.href,
      page_title: document?.title,
    });
  };
  updateUser(`currentLesson=${props?.cardAddress?.lessonId}`, props?.globals?.env);

  const endLesson = () => {
    // TODO: Implement greetings!
    console.log("Ти завершив урок!");
    baseUrl && navigateToLessonsPlan(baseUrl);
  };

  const next = (id) => {
    const isLastCard =
      cardsSlider?.current?.props?.children?.length === parseInt(id);
    if (isLastCard) {
      endLesson();
    } else {
      cardsSlider?.current?.slickNext();
    }
  };

  const goTo = async (index) => {
    await cardsSlider?.current?.slickGoTo(index);
  };

  const toggleCunningMode = async () => {
    setState({ ...state, ...{ cunningMode: !state?.cunningMode } });
    localStorage?.setItem("cunningMode", !state?.cunningMode);
    updateUser(`cunningMode=${state?.cunningMode}`, globals?.env);
  };

  const beforeChange = async (oldIndex, newIndex) => {
    document.title = `Урок ${props?.cardAddress?.lessonId}, картка ${
      newIndex + 1
    }. — «У мене виходить!» | Ребус-метод. — ${obfuscateEmail(
      authUser?.email
    )}`;
    reportAnalytics();
    const isAfterLastCard = oldIndex > 2 && newIndex === 0;
    const isLastCard = newIndex + 1 === state?.lesson?.cards?.length;
    const isBeforeFirstCard = oldIndex === 0 && newIndex > 2;
    // if it's an already seen lesson
    // and it's not the last lesson
    // and the user is moving forward
    const currentLesson = state?.cardAddress?.lessonId;
    const isCurrentlyTheLastAvailableLesson =
      currentLesson === state?.authUser?.study?.availableLesson;
    const isNotTheLastCourseLesson =
      currentLesson !== props?.globals?.maxLesson;
    // will be topped up by the payment gateway
    const isNotTheLastPaidLesson =
      currentLesson !== props?.authUser?.study?.paidLesson;
    const isUserMovingForward = oldIndex < newIndex;
    const isNewCardToTheUser =
      state?.authUser?.study?.availableCard < newIndex + 1 &&
      isCurrentlyTheLastAvailableLesson;
    updateUser(`countView=true`, globals?.env);
    isNewCardToTheUser &&
      !isLastCard &&
      isUserMovingForward &&
      updateUser(`availableCard=${newIndex + 1}`, globals?.env);
    if (
      isCurrentlyTheLastAvailableLesson &&
      isNotTheLastCourseLesson &&
      isNotTheLastPaidLesson &&
      isUserMovingForward &&
      isLastCard
    ) {
      const nextAvailableLesson = currentLesson + 1;
      const nextAvailableCard = 1;
      await updateUser(
        `availableLesson=${nextAvailableLesson}&availableCard=${nextAvailableCard}`,
        globals?.env
      );
    }
    if (startSpecialOfferCountDownCardIndex === newIndex)
      updateUser("startSpecialOfferCountDown=true", globals?.env);
    if (isBeforeFirstCard || isAfterLastCard) {
      setIsLoading(true);
      endLesson();
    }
  };

  const afterChange = (index) => {
    updateUrl(index + 1);
  };

  const { lesson, streamlinedArticles, globals, authUser } = props;
  let settings = {
    dots: false,
    arrows: true,
    adaptiveHeight: true,
    focusOnSelect: false,
    infinite: true,
    speed: 250,
    slidesToShow: 1,
    slidesToScroll: 1,
    lazyLoad: false,
    initialSlide: state?.cardAddress?.cardId - 1 ?? 1,
    beforeChange,
    afterChange,
  };
  const cardAddress = state?.cardAddress;
  const lessonId = state?.lesson?.id;
  return cardAddress?.lessonId <= authUser?.study?.paidLesson ||
    globals?.env?.isLF ? (
    <>
      <div className="site-content" data-site-content>
        {!isLoading ? (
          <Slider
            ref={(slider) => (cardsSlider.current = slider)}
            {...settings}
            onReInit={() => {
              // fixing 3rd party component layout issue
              // setting this parameter in styles ruins initial rendering
              // setting it here fixes vertical alignment
              // but as Safari on iOS does support dynamic viewport height,
              // it gets disabled
              const isIos = /iP(ad|od|hone)/i.test(window.navigator.userAgent);
              const isFlexFixEnabled = !(
                isIos && window.innerHeight < window.innerWidth
              );
              if (isFlexFixEnabled) {
                document.querySelector("[data-site-content]").style.display =
                  "flex";
              } else {
                document.querySelector("[data-site-content]").style.display =
                  "block";
              }
            }}
          >
            {lesson?.cards?.map((currentCard, index) => {
              const streamlinedArticle =
                currentCard?.type === "info-page"
                  ? streamlinedArticles.filter(
                      (streamlinedArticle) =>
                        streamlinedArticle?.articleId ===
                        currentCard?.templateId
                    )[0]
                  : null;
              const card = {
                ...currentCard,
                ...{
                  streamlinedArticle,
                  next,
                  goTo,
                  currentIndex: index,
                  globals,
                  authUser,
                },
              };
              // check if the card should start the count down timer
              // isSpecialOfferValid is a number, when less than 24 left
              card?.streamlinedArticle?.startSpecialOfferCountDown &&
                typeof authUser?.isSpecialOfferValid === "boolean" &&
                startSpecialOfferCountDownCardIndex === null &&
                setStartSpecialOfferCountDownCardIndex(index);
              // skip a card that should be hidden for paid users
              const isRendered =
                (card?.streamlinedArticle?.isSkippedWhenPaid &&
                  cardAddress?.lessonId === authUser?.study?.paidLesson) ||
                !card?.streamlinedArticle?.isSkippedWhenPaid;
              return (
                isRendered && (
                  <Card
                    key={card.id}
                    card={card}
                    thisCardIndex={index ?? 0}
                    nextCard={lesson?.cards[index + 1]}
                    lessonId={lessonId}
                    cardAddress={cardAddress}
                    authUser={authUser}
                    baseUrl={baseUrl}
                    cunningMode={state?.cunningMode}
                    toggleCunningMode={toggleCunningMode}
                  />
                )
              );
            })}
          </Slider>
        ) : (
          <div className="centered">{lang?.loading}</div>
        )}
      </div>
      <div className="rotate-your-device">
        {lang?.rotateYourDevice} <span className="rotated-phone">📱</span>
      </div>
    </>
  ) : (
    <div className="centered">{lang?.theLessonIsNotYetPaid}</div>
  );
};

Cards.propTypes = {
  lesson: PropTypes.object,
  cardAddress: PropTypes.object,
};

export default Cards;
